import { useEffect, useMemo, useState } from 'react';
import { Search, Filter, Briefcase } from 'lucide-react';
import { useJobStore } from '../lib/store';
import JobCard from '../components/JobCard';
import EmptyState from '../components/EmptyState';

const STATUSES = ['All', 'Pending', 'In Progress', 'Awaiting Parts', 'Ready to Invoice', 'Completed', 'Invoiced'];

export default function Jobs() {
  const { jobs, fetchJobs, loading } = useJobStore();
  const [statusFilter, setStatusFilter] = useState('All');
  const [q, setQ] = useState('');

  useEffect(() => {
    fetchJobs('All', 'All');
  }, [fetchJobs]);

  const filtered = useMemo(() => {
    let list = jobs;
    if (statusFilter !== 'All') list = list.filter((j) => j.status === statusFilter);
    const ql = q.trim().toLowerCase();
    if (ql) {
      list = list.filter(
        (j) =>
          j.title?.toLowerCase().includes(ql) ||
          j.client_name?.toLowerCase().includes(ql) ||
          j.job_number?.toLowerCase().includes(ql) ||
          j.assigned_to?.toLowerCase().includes(ql)
      );
    }
    return list;
  }, [jobs, statusFilter, q]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Jobs</h1>
        <p className="text-slate-500 mt-1">All bookings and their current status.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search jobs, clients, job numbers…"
            className="input pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input pl-9 pr-8 appearance-none min-w-[200px] cursor-pointer"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Status chips (mobile-friendly quick filter) */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {STATUSES.map((s) => {
          const active = statusFilter === s;
          const count = s === 'All' ? jobs.length : jobs.filter((j) => j.status === s).length;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                active
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {s} <span className="ml-1 opacity-80">{count}</span>
            </button>
          );
        })}
      </div>

      {loading && jobs.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="animate-spin h-6 w-6 rounded-full border-3 border-brand-600 border-t-transparent mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={Briefcase}
            title="No jobs found"
            subtitle={q ? 'Try a different search.' : 'Nothing matches this filter.'}
          />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
          {filtered.map((job) => (
            <JobCard key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  );
}
